import React, { useCallback, useEffect, useRef } from "react";
import { IconPosition, SpecialEffectDrawer } from "@obsbot/remo-utils";
import "./index.less";
import { SpecialEffectIconUsed } from "../SpecialEffect";
import Lottie from "lottie-web";
import { SOUND_MAP } from "../../VideoWindowLayout";
import { InterActionMessageType } from "@obsbot/remo-stores";
import { deepClone, SpecialEffectRenderDo } from "../../../utils/LottieAnimationHandler";

export interface SpecialEffectCanvasProps {
    size?: number;
    renderIcons?: InterActionMessageType[];
    className?: string;
    speed?: number;
    isShowSpecialIcon: boolean;
    duration?: number;
    onAnimationEnd?: () => void;
    setRenderIcons?: (icon: InterActionMessageType[]) => void;
    getPosition?: (domRect: DOMRect) => IconPosition[];
}

export const SpecialEffectCanvas: React.FC<SpecialEffectCanvasProps> = ({
    className,
    renderIcons,
    size = 80,
    speed = 2.4,
    isShowSpecialIcon,
    onAnimationEnd,
    setRenderIcons,
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const divRef = useRef<HTMLDivElement>(null);
    const parentSizeRef = useRef<DOMRect | null>(null);
    const audioRefs = useRef<{ [key: string]: HTMLAudioElement }>({});

    const specialEffectDrawer = useRef<SpecialEffectDrawer | null>(null);

    const isAnimating = useRef(false);
    const realSpecialList = useRef<InterActionMessageType[]>([]);

    const initCanvas = useCallback(async (): Promise<void> => {
        const canvas = canvasRef.current;
        if (canvas) {
            // 获取父级元素的大小
            const parent = canvas.parentElement;
            if (parent) {
                parentSizeRef.current = parent.getBoundingClientRect();
                canvas.width = parent.clientWidth;
                canvas.height = parent.clientHeight;
            }

            const ctx = canvas.getContext("2d");
            if (ctx) {
                ctx.fillStyle = "rgba(255, 255, 255, 0)";
            }
        }
    }, []);

    useEffect(() => {
        // 确保只加载一次(预加载所有音频)
        const allSoundPaths = new Set<string>();
        // 从所有音频列表加载
        Object.values(SOUND_MAP).forEach(path => allSoundPaths.add(path));
        allSoundPaths.forEach(src => {
            if (!audioRefs.current[src]) {
                const audio = new Audio(src);
                audio.load(); // 预加载音频
                audioRefs.current[src] = audio;
            }
        });
        return;
    }, []);

    useEffect(() => {
        if (canvasRef.current) {
            initCanvas();

            specialEffectDrawer.current = new SpecialEffectDrawer(canvasRef.current, size, speed);
            onAnimationEnd && specialEffectDrawer.current.setOnAnimationEnd(onAnimationEnd);
        }
        return () => {
            specialEffectDrawer.current?.dispose();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (specialEffectDrawer.current) {
            specialEffectDrawer.current.setSize(size);
            specialEffectDrawer.current.setSpeed(speed);
            onAnimationEnd && specialEffectDrawer.current.setOnAnimationEnd(onAnimationEnd);
        }
    }, [size, speed, onAnimationEnd]);

    const doRenderSpecial = useCallback(
        (_drawer: SpecialEffectDrawer) => {
            if (isAnimating.current) {
                if (renderIcons) {
                    realSpecialList.current = renderIcons;
                }
                return;
            }
            console.log("[OVERLAY DEBUG]展示特效");
            if (renderIcons && renderIcons.length) {
                if (realSpecialList.current.length === 0) {
                    realSpecialList.current = renderIcons;
                }
                isAnimating.current = true;
                const specialEffects: InterActionMessageType[] = JSON.parse(
                    JSON.stringify(realSpecialList.current),
                );
                const currentSpecialEffect = specialEffects.slice(0, 1)[0];
                const soundPath = currentSpecialEffect.soundEffect;
                console.log("【WEB DEBUG】游戏覆盖下的音效", soundPath);
                if (soundPath) {
                    const soundEffect = SOUND_MAP[soundPath as SpecialEffectIconUsed];
                    const audio = audioRefs.current[soundEffect];
                    if (audio) {
                        audio.currentTime = 0; // 每次播放都从头开始
                        audio
                            .play()
                            .catch(e => console.error("Error playing sound:", soundPath, e));
                    } else {
                        console.log(
                            `[WEB DEBUG]音频不存在或预加载失败 ${currentSpecialEffect.actionID}`,
                        );
                    }
                }
                if (divRef.current) {
                    const animationData = deepClone(currentSpecialEffect.icon);
                    const animation = Lottie.loadAnimation({
                        container: divRef.current,
                        renderer: "canvas",
                        loop: false,
                        autoplay: true,
                        animationData,
                    });
                    animation.setSubframe(false);
                    // 处理额外的Lottie播放逻辑
                    const extraDoing =
                        SpecialEffectRenderDo[
                            currentSpecialEffect.soundEffect as SpecialEffectIconUsed
                        ];
                    extraDoing?.(animation);
                    animation.addEventListener("complete", () => {
                        animation.destroy();
                        const remaining = realSpecialList.current.slice(1);
                        realSpecialList.current = remaining;
                        setRenderIcons && setRenderIcons(remaining);
                        if (remaining.length > 0) {
                            setTimeout(() => {
                                isAnimating.current = false;
                                doRenderSpecial(_drawer);
                            }, 0);
                        } else {
                            isAnimating.current = false;
                        }
                    });
                }
            }
        },
        [renderIcons, setRenderIcons],
    );

    useEffect(() => {
        if (
            isShowSpecialIcon &&
            renderIcons &&
            (renderIcons.length || realSpecialList.current.length)
        ) {
            const drawer = specialEffectDrawer.current;
            if (drawer && parentSizeRef.current) {
                doRenderSpecial(drawer);
            }
        }
    }, [doRenderSpecial, isShowSpecialIcon, renderIcons]);

    return (
        <>
            <canvas ref={canvasRef} className={"special-effect-canvas " + (className || "")} />
            <div ref={divRef} className="special-effect-div"></div>
        </>
    );
};
